// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// rules.ts — the whole rulebook, as a pure module. No DOM, no engine, no randomness.
//
// THE TABLE HAS NO BAG. That is the single most important thing about this file. Once the racks are
// dealt, a round is a PERFECT-INFORMATION game with no chance nodes in it at all — nobody consults
// the bag until the round ends. So the search horizon is the end of the current round, the bag never
// enters the tree, and `Table` carries `bagCount` (a number anyone at the table can work out by
// subtraction) and nothing else. A clairvoyant bot is therefore a type error rather than a matter of
// discipline: there is no bag here to peek at. The bag lives in `game.ts`, which owns the seed.
//
// The bag is COUNTS, not an ordered array, and a draw is a seeded weighted pick. That makes the
// reflood (discard back into the bag when it empties mid-deal) canonical by construction — there is
// no insertion order for two peers to disagree about, which is the shape of desync that lands
// exactly when a game is most alive.

import {
  CELLS,
  COURSES,
  KINDS,
  RACK_SIZE,
  SIDE,
  SPOIL_SLOTS,
  ROW_BONUS,
  COL_BONUS,
  KIND_BONUS,
  TILES_PER_KIND,
  courseCap,
  printedCol,
  racksFor,
  spoilPenalty,
} from './kinds';
import type { Mode } from './modes';

/** The Sill, as a take source. */
export const SILL = -1;
/** The Spoil, as a load destination. */
export const SPOIL = -1;
/** No column nominated (every mode but Freehand, and Freehand once a course already holds one). */
export const NO_COL = -1;

export interface Move {
  /** A rack index, or SILL. */
  readonly src: number;
  readonly kind: number;
  /** A course index 0..4, or SPOIL. */
  readonly dest: number;
  /** Freehand only, and only on the load that first fills an empty course. Otherwise NO_COL. */
  readonly col: number;
}

export type EndReason = 'row' | 'drained' | 'cap';

export interface LayEvent {
  readonly seat: number;
  readonly row: number;
  readonly col: number;
  readonly kind: number;
  readonly gain: number;
  /** The cells that made up the runs, for the UI to light in order. */
  readonly run: readonly number[];
}

export interface PenaltyEvent {
  readonly seat: number;
  readonly items: number;
  readonly loss: number;
  readonly from: number;
  readonly to: number;
}

export interface RoundResult {
  readonly lays: readonly LayEvent[];
  readonly penalties: readonly PenaltyEvent[];
}

export interface TakeResult {
  readonly kind: number;
  /** How many tiles the take yielded. */
  readonly took: number;
  /** How many of them would not fit and fell to the Spoil. */
  readonly overflow: number;
  /** Course tiles that left play because a full course laid one and discarded the rest. */
  readonly discarded: number;
  readonly lays: readonly LayEvent[];
}

// ---------------------------------------------------------------- the table

export class Table {
  readonly mode: Mode;
  readonly players: number;
  readonly rackCount: number;

  /** rackCount * KINDS counts. A rack is spent when all five are zero. */
  readonly racks: Int8Array;
  readonly sill: Int8Array;
  /** Is the Trowel still unclaimed in the Sill this round? */
  trowelInSill = true;

  /** players * COURSES */
  readonly courseKind: Int8Array;
  readonly courseCount: Int8Array;
  /** Freehand: the nominated column for this course, or NO_COL. */
  readonly courseCol: Int8Array;
  /** Quickfire: a bitmask per seat of courses that already laid this round and are locked. */
  readonly fired: Uint8Array;

  /** players * CELLS, holding a kind or -1. */
  readonly mosaic: Int8Array;
  /** players * SIDE, a 5-bit occupancy mask per row / per column. */
  readonly rowBits: Uint8Array;
  readonly colBits: Uint8Array;

  readonly spoilTiles: Int8Array;
  readonly trowel: Uint8Array;
  readonly score: Int16Array;

  seat = 0;
  /** Who opened this round, and so who opens the next unless the Trowel changes hands. */
  lead = 0;
  round = 1;
  over = false;
  endReason: EndReason | null = null;

  /** Tiles neither on the table nor on anybody's board: bag + discard. Public by subtraction. */
  bagCount = 0;

  constructor(mode: Mode, players: number) {
    this.mode = mode;
    this.players = players;
    this.rackCount = racksFor(players);
    this.racks = new Int8Array(this.rackCount * KINDS);
    this.sill = new Int8Array(KINDS);
    this.courseKind = new Int8Array(players * COURSES).fill(-1);
    this.courseCount = new Int8Array(players * COURSES);
    this.courseCol = new Int8Array(players * COURSES).fill(NO_COL);
    this.fired = new Uint8Array(players);
    this.mosaic = new Int8Array(players * CELLS).fill(-1);
    this.rowBits = new Uint8Array(players * SIDE);
    this.colBits = new Uint8Array(players * SIDE);
    this.spoilTiles = new Int8Array(players);
    this.trowel = new Uint8Array(players);
    this.score = new Int16Array(players);
  }

  /** A detached copy. Search allocates these once per ply and reuses them. */
  clone(): Table {
    const t = new Table(this.mode, this.players);
    this.copyInto(t);
    return t;
  }

  copyInto(t: Table): void {
    t.racks.set(this.racks);
    t.sill.set(this.sill);
    t.trowelInSill = this.trowelInSill;
    t.courseKind.set(this.courseKind);
    t.courseCount.set(this.courseCount);
    t.courseCol.set(this.courseCol);
    t.fired.set(this.fired);
    t.mosaic.set(this.mosaic);
    t.rowBits.set(this.rowBits);
    t.colBits.set(this.colBits);
    t.spoilTiles.set(this.spoilTiles);
    t.trowel.set(this.trowel);
    t.score.set(this.score);
    t.seat = this.seat;
    t.lead = this.lead;
    t.round = this.round;
    t.over = this.over;
    t.endReason = this.endReason;
    t.bagCount = this.bagCount;
  }

  rackTiles(r: number): number {
    let n = 0;
    for (let k = 0; k < KINDS; k++) n += this.racks[r * KINDS + k];
    return n;
  }

  sillTiles(): number {
    let n = 0;
    for (let k = 0; k < KINDS; k++) n += this.sill[k];
    return n;
  }

  tableTiles(): number {
    let n = this.sillTiles();
    for (let r = 0; r < this.rackCount; r++) n += this.rackTiles(r);
    return n;
  }

  /** The round is over when there is nothing left to take. */
  roundOver(): boolean {
    return this.tableTiles() === 0;
  }

  cell(seat: number, r: number, c: number): number {
    return this.mosaic[seat * CELLS + r * SIDE + c];
  }

  /** Does this seat's Mosaic row already hold this kind? */
  rowHasKind(seat: number, r: number, kind: number): boolean {
    for (let c = 0; c < SIDE; c++) if (this.cell(seat, r, c) === kind) return true;
    return false;
  }

  /** Laid OR reserved: Freehand's column rule counts a rival reservation of your own making. */
  colHasKind(seat: number, c: number, kind: number, ignoreRow: number): boolean {
    for (let r = 0; r < SIDE; r++) {
      if (this.cell(seat, r, c) === kind) return true;
      if (r === ignoreRow) continue;
      const i = seat * COURSES + r;
      if (this.courseCol[i] === c && this.courseKind[i] === kind) return true;
    }
    return false;
  }

  /** The columns a Freehand course could still nominate for this kind. */
  legalCols(seat: number, row: number, kind: number): number[] {
    const out: number[] = [];
    for (let c = 0; c < SIDE; c++) {
      if (this.cell(seat, row, c) !== -1) continue;
      if (this.colHasKind(seat, c, kind, row)) continue;
      out.push(c);
    }
    return out;
  }

  /** Where a full course of `kind` in `row` will lay. -1 if it has no home (Freehand, unnominated). */
  layTarget(seat: number, row: number, kind: number): number {
    if (this.mode.printed) return printedCol(row, kind);
    return this.courseCol[seat * COURSES + row];
  }

  hasFullRow(seat: number): boolean {
    for (let r = 0; r < SIDE; r++) if (this.rowBits[seat * SIDE + r] === 0b11111) return true;
    return false;
  }
}

// ---------------------------------------------------------------- scoring

/** The contiguous run through index `i` of a 5-bit occupancy mask that already includes `i`. */
export function runLen(bits: number, i: number): number {
  let len = 1;
  for (let j = i - 1; j >= 0 && (bits >> j) & 1; j--) len++;
  for (let j = i + 1; j < SIDE && (bits >> j) & 1; j++) len++;
  return len;
}

/** Points for laying into (r,c), given the masks AFTER the tile is in place. */
export function layScore(rowBits: number, colBits: number, r: number, c: number): number {
  const h = runLen(rowBits, c);
  const v = runLen(colBits, r);
  const s = (h > 1 ? h : 0) + (v > 1 ? v : 0);
  return s === 0 ? 1 : s;
}

/** What laying a full course of `kind` in `row` would score right now. 0 if it cannot lay. */
export function projectedLayScore(t: Table, seat: number, row: number, kind: number): number {
  const col = t.layTarget(seat, row, kind);
  if (col < 0 || t.cell(seat, row, col) !== -1) return 0;
  const rb = t.rowBits[seat * SIDE + row] | (1 << col);
  const cb = t.colBits[seat * SIDE + col] | (1 << row);
  return layScore(rb, cb, row, col);
}

export function endBonus(t: Table, seat: number): number {
  let bonus = 0;
  for (let r = 0; r < SIDE; r++) if (t.rowBits[seat * SIDE + r] === 0b11111) bonus += ROW_BONUS;
  for (let c = 0; c < SIDE; c++) if (t.colBits[seat * SIDE + c] === 0b11111) bonus += COL_BONUS;
  const counts = new Int8Array(KINDS);
  for (let i = 0; i < CELLS; i++) {
    const k = t.mosaic[seat * CELLS + i];
    if (k >= 0) counts[k]++;
  }
  for (let k = 0; k < KINDS; k++) if (counts[k] === SIDE) bonus += KIND_BONUS;
  return bonus;
}

export function finalScore(t: Table, seat: number): number {
  return Math.max(0, t.score[seat] + endBonus(t, seat));
}

/** Complete Mosaic rows, the first tie-break. */
export function completeRows(t: Table, seat: number): number {
  let n = 0;
  for (let r = 0; r < SIDE; r++) if (t.rowBits[seat * SIDE + r] === 0b11111) n++;
  return n;
}

/**
 * Seats in finishing order. Ties break on complete rows, then on fewest tiles ever spoiled, then on
 * seat. A tie-break has to exist: without one "who was leading after round 1" is uncomputable, and
 * after round 1 in Setwork a tie is the MODAL outcome.
 */
export function ranking(t: Table, spoiled: readonly number[]): number[] {
  const seats = [];
  for (let s = 0; s < t.players; s++) seats.push(s);
  return seats.sort((a, b) => {
    const d = finalScore(t, b) - finalScore(t, a);
    if (d !== 0) return d;
    const r = completeRows(t, b) - completeRows(t, a);
    if (r !== 0) return r;
    const sp = (spoiled[a] ?? 0) - (spoiled[b] ?? 0);
    if (sp !== 0) return sp;
    return a - b;
  });
}

// ---------------------------------------------------------------- legality

/** Every (source, kind) pair that can be taken right now. */
export function legalTakes(t: Table): { src: number; kind: number; count: number }[] {
  const out: { src: number; kind: number; count: number }[] = [];
  if (t.over) return out;
  for (let r = 0; r < t.rackCount; r++) {
    for (let k = 0; k < KINDS; k++) {
      const n = t.racks[r * KINDS + k];
      if (n > 0) out.push({ src: r, kind: k, count: n });
    }
  }
  for (let k = 0; k < KINDS; k++) {
    if (t.sill[k] > 0) out.push({ src: SILL, kind: k, count: t.sill[k] });
  }
  return out;
}

export function takeCount(t: Table, src: number, kind: number): number {
  if (kind < 0 || kind >= KINDS) return 0;
  if (src === SILL) return t.sill[kind];
  if (src < 0 || src >= t.rackCount) return 0;
  return t.racks[src * KINDS + kind];
}

/**
 * Can this seat load `kind` into this course?
 *
 * The Freehand clause is the one an adversarial pass had to find: row-legality and column-legality
 * can disagree. A kind can be absent from row r (so the source rule says "legal") while every one of
 * the five columns is already spoken for by that kind elsewhere — which would open a destination
 * with five dimmed column chips and no way forward, and a rules/audit divergence at lay time. So a
 * load is legal IFF a column exists for it.
 */
export function canLoad(t: Table, seat: number, course: number, kind: number): boolean {
  if (course < 0 || course >= COURSES) return false;
  if ((t.fired[seat] >> course) & 1) return false; // Quickfire: laid already, locked till round end
  const i = seat * COURSES + course;
  if (t.courseCount[i] >= courseCap(course)) return false;
  if (t.courseKind[i] !== -1 && t.courseKind[i] !== kind) return false;
  if (t.rowHasKind(seat, course, kind)) return false;
  if (!t.mode.printed && t.courseCol[i] === NO_COL && t.legalCols(seat, course, kind).length === 0) {
    return false;
  }
  return true;
}

export function isLegal(t: Table, m: Move): boolean {
  if (t.over) return false;
  const n = takeCount(t, m.src, m.kind);
  if (n <= 0) return false;
  if (m.dest === SPOIL) return m.col === NO_COL;
  if (!canLoad(t, t.seat, m.dest, m.kind)) return false;
  const i = t.seat * COURSES + m.dest;
  if (t.mode.printed) return m.col === NO_COL;
  // Freehand: nominate exactly when the course does not already carry a reservation.
  if (t.courseCol[i] !== NO_COL) return m.col === NO_COL;
  return t.legalCols(t.seat, m.dest, m.kind).includes(m.col);
}

/** Every legal move. Used by tests, the audit and the random control policy — not by the search. */
export function legalMoves(t: Table): Move[] {
  const out: Move[] = [];
  if (t.over) return out;
  for (const take of legalTakes(t)) {
    out.push({ src: take.src, kind: take.kind, dest: SPOIL, col: NO_COL });
    for (let c = 0; c < COURSES; c++) {
      if (!canLoad(t, t.seat, c, take.kind)) continue;
      const i = t.seat * COURSES + c;
      if (t.mode.printed || t.courseCol[i] !== NO_COL) {
        out.push({ src: take.src, kind: take.kind, dest: c, col: NO_COL });
      } else {
        for (const col of t.legalCols(t.seat, c, take.kind)) {
          out.push({ src: take.src, kind: take.kind, dest: c, col });
        }
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------- applying a move

/** Put a tile into (row,col) for a seat and return what it scored. */
function placeTile(t: Table, seat: number, row: number, col: number, kind: number): LayEvent {
  t.mosaic[seat * CELLS + row * SIDE + col] = kind;
  t.rowBits[seat * SIDE + row] |= 1 << col;
  t.colBits[seat * SIDE + col] |= 1 << row;
  const rb = t.rowBits[seat * SIDE + row];
  const cb = t.colBits[seat * SIDE + col];
  const gain = layScore(rb, cb, row, col);
  t.score[seat] += gain;
  const run: number[] = [];
  const h = runLen(rb, col);
  const v = runLen(cb, row);
  if (h > 1) for (let c = 0; c < SIDE; c++) if ((rb >> c) & 1) run.push(row * SIDE + c);
  if (v > 1) for (let r = 0; r < SIDE; r++) if ((cb >> r) & 1) run.push(r * SIDE + col);
  if (run.length === 0) run.push(row * SIDE + col);
  return { seat, row, col, kind, gain, run };
}

/**
 * Play a move. The caller must have checked `isLegal`; `apply` trusts it (the search calls this
 * millions of times). Returns the tiles that left play, so the caller can keep the discard honest,
 * plus any lay that Quickfire triggered on the spot.
 */
export function apply(t: Table, m: Move): TakeResult {
  const seat = t.seat;
  const n = takeCount(t, m.src, m.kind);
  const lays: LayEvent[] = [];
  let discarded = 0;

  if (m.src === SILL) {
    t.sill[m.kind] = 0;
    if (t.trowelInSill) {
      t.trowelInSill = false;
      t.trowel[seat] = 1;
      t.lead = seat; // whoever picks up the Trowel opens the next round
    }
  } else {
    const base = m.src * KINDS;
    t.racks[base + m.kind] = 0;
    for (let k = 0; k < KINDS; k++) {
      if (t.racks[base + k] > 0) {
        t.sill[k] += t.racks[base + k];
        t.racks[base + k] = 0;
      }
    }
  }

  let overflow = n;
  if (m.dest !== SPOIL) {
    const i = seat * COURSES + m.dest;
    const room = courseCap(m.dest) - t.courseCount[i];
    const placed = Math.min(n, room);
    overflow = n - placed;
    t.courseKind[i] = m.kind;
    t.courseCount[i] += placed;
    if (!t.mode.printed && t.courseCol[i] === NO_COL) t.courseCol[i] = m.col;

    if (t.mode.instantLay && t.courseCount[i] === courseCap(m.dest)) {
      const col = t.layTarget(seat, m.dest, m.kind);
      lays.push(placeTile(t, seat, m.dest, col, m.kind));
      discarded += courseCap(m.dest) - 1;
      t.courseKind[i] = -1;
      t.courseCount[i] = 0;
      t.courseCol[i] = NO_COL;
      t.fired[seat] |= 1 << m.dest;
    }
  }
  t.spoilTiles[seat] += overflow;
  const out: TakeResult = { kind: m.kind, took: n, overflow, discarded, lays };

  if (t.mode.instantEnd && t.hasFullRow(seat)) {
    t.over = true;
    t.endReason = 'row';
    return out;
  }

  t.seat = (seat + 1) % t.players;
  return out;
}

/**
 * End of round: every FULL course lays one tile, then penalties are taken once, then the Spoil is
 * swept.
 *
 * The sweep is not a detail. Without it the ladder is cumulative for the whole game (round 2 opens
 * at slot 3), tiles leave circulation monotonically, and a 4-player game eventually deals nothing at
 * all and loops empty rounds forever. Two omissions that assemble into a non-termination path.
 */
export function resolveRound(t: Table): RoundResult & { discarded: number[] } {
  const lays: LayEvent[] = [];
  const penalties: PenaltyEvent[] = [];
  const discarded = new Array(KINDS).fill(0) as number[];

  if (!t.mode.instantLay) {
    // Courses 0 -> 4, i.e. row 1 to row 5. Pinned: adjacency scoring is order-dependent.
    for (let seat = 0; seat < t.players; seat++) {
      for (let c = 0; c < COURSES; c++) {
        const i = seat * COURSES + c;
        if (t.courseCount[i] !== courseCap(c)) continue;
        const kind = t.courseKind[i];
        const col = t.layTarget(seat, c, kind);
        lays.push(placeTile(t, seat, c, col, kind));
        discarded[kind] += courseCap(c) - 1;
        t.courseKind[i] = -1;
        t.courseCount[i] = 0;
        t.courseCol[i] = NO_COL;
      }
    }
  }

  for (let seat = 0; seat < t.players; seat++) {
    const items = t.spoilTiles[seat] + t.trowel[seat];
    const loss = spoilPenalty(items);
    const from = t.score[seat];
    const to = Math.max(0, from - loss);
    t.score[seat] = to;
    if (items > 0) penalties.push({ seat, items, loss, from, to });
    t.spoilTiles[seat] = 0;
    t.trowel[seat] = 0;
    t.fired[seat] = 0;
  }
  t.trowelInSill = true;

  if (!t.over) {
    for (let seat = 0; seat < t.players; seat++) {
      if (t.hasFullRow(seat)) {
        t.over = true;
        t.endReason = 'row';
        break;
      }
    }
  }
  return { lays, penalties, discarded };
}

/** Tiles a seat holds that are not on the Mosaic: courses plus the Spoil. For conservation. */
export function heldTiles(t: Table, seat: number): number {
  let n = t.spoilTiles[seat];
  for (let c = 0; c < COURSES; c++) n += t.courseCount[seat * COURSES + c];
  return n;
}

/** Tiles of each kind that anybody can SEE. The bot infers what is left from this — card counting,
 *  which is a legitimate skill, rather than reading the bag, which is not. */
export function seenCounts(t: Table): Int16Array {
  const seen = new Int16Array(KINDS);
  for (let r = 0; r < t.rackCount; r++) {
    for (let k = 0; k < KINDS; k++) seen[k] += t.racks[r * KINDS + k];
  }
  for (let k = 0; k < KINDS; k++) seen[k] += t.sill[k];
  for (let s = 0; s < t.players; s++) {
    for (let c = 0; c < COURSES; c++) {
      const i = s * COURSES + c;
      if (t.courseKind[i] >= 0) seen[t.courseKind[i]] += t.courseCount[i];
    }
    for (let i = 0; i < CELLS; i++) {
      const k = t.mosaic[s * CELLS + i];
      if (k >= 0) seen[k]++;
    }
  }
  return seen;
}

/** An upper bound on how many of `kind` could still reach the table. */
export function unseen(t: Table, kind: number): number {
  return Math.max(0, TILES_PER_KIND - seenCounts(t)[kind]);
}

// ---------------------------------------------------------------- ply packing

/** src+1 : 4 bits | kind : 3 | dest+1 : 3 | col+1 : 3. A whole game is ~120 of these. */
export function plyOf(m: Move): number {
  return ((m.src + 1) << 9) | (m.kind << 6) | ((m.dest + 1) << 3) | (m.col + 1);
}

export function moveOf(ply: number): Move | null {
  if (!Number.isInteger(ply) || ply < 0 || ply > 0x1fff) return null;
  const src = ((ply >> 9) & 0xf) - 1;
  const kind = (ply >> 6) & 0x7;
  const dest = ((ply >> 3) & 0x7) - 1;
  const col = (ply & 0x7) - 1;
  if (kind >= KINDS || dest >= COURSES || col >= SIDE || src < -1 || dest < -1 || col < -1) {
    return null;
  }
  return { src, kind, dest, col };
}

export { RACK_SIZE, SPOIL_SLOTS, COURSES, SIDE, KINDS };
